import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import webservice from "../../../services/webservice";
import "./QuestionList.css";
import { AesDecrypt } from "../../../services/crypto";
import Swal from "sweetalert2";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";

export default function QuestionList() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [activeTab, setActiveTab] = useState("general"); // Default to general tab

    // Edit form state
    const [editForm, setEditForm] = useState({
        questionId: "",
        questionText: "",
        questionType: "text",
        options: [],
        isGeneral: false,
        isActive: true,
        relatedTo: "",
        helpText: "",
        next: []
    });

    const [newOption, setNewOption] = useState("");
    const [saving, setSaving] = useState(false);

    // Tabs configuration
    const tabs = [
        { id: "general", label: "General Questions", filter: (q) => q.isGeneral && (!q.relatedTo || q.relatedTo === "") },
        { id: "mood_monitor", label: "Mood Monitor", filter: (q) => q.relatedTo === "mood_monitor" },
        { id: "blood_pressure", label: "Blood Pressure", filter: (q) => q.relatedTo === "blood_pressure" },
        { id: "diabetes", label: "Diabetes", filter: (q) => q.relatedTo === "diabetes" },
        { id: "water_intake", label: "Water Intake", filter: (q) => q.relatedTo === "water_intake" },
        { id: "bmi", label: "BMI", filter: (q) => q.relatedTo === "bmi" },
        { id: "period_tracker", label: "Period Tracker", filter: (q) => q.relatedTo === "period_tracker" }
    ];

    // Question type options for React Select
    const questionTypeOptions = [
        { value: "text", label: "Text Input" },
        { value: "date", label: "Date" },
        { value: "gender", label: "Gender" },
        { value: "single_select", label: "Single Select" },
        { value: "multi_select", label: "Multi Select" },
        { value: "height", label: "Height" },
        { value: "weight", label: "Weight" },
        { value: "time", label: "Time" },
        { value: "dropdown", label: "Dropdown" },
        { value: "numeric", label: "Numeric" },
        { value: "date_optional", label: "Date Optional" }
    ];

    // Related to options for React Select (filter out empty option for the dropdown)
    const relatedToOptions = [
        { value: "", label: "General" },
        { value: "mood_monitor", label: "Mood Monitor" },
        { value: "blood_pressure", label: "Blood Pressure" },
        { value: "diabetes", label: "Diabetes" },
        { value: "water_intake", label: "Water Intake" },
        { value: "bmi", label: "BMI" },
        { value: "period_tracker", label: "Period Tracker" }
    ];

    // Filter options
    const filterTypeOptions = [
        { value: "all", label: "All Types" },
        { value: "text", label: "Text" },
        { value: "date", label: "Date" },
        { value: "gender", label: "Gender" },
        { value: "single_select", label: "Single Select" },
        { value: "multi_select", label: "Multi Select" },
        { value: "height", label: "Height" },
        { value: "weight", label: "Weight" },
        { value: "time", label: "Time" },
        { value: "dropdown", label: "Dropdown" },
        { value: "numeric", label: "Numeric" },
        { value: "date_optional", label: "Date Optional" }
    ];

    const filterStatusOptions = [
        { value: "all", label: "All Status" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" }
    ];

    const isFetching = useRef(false);
    const hasFetched = useRef(false);

    // Fetch questions from API
    const fetchQuestions = async () => {
        if (isFetching.current) return;
        try {
            setLoading(true);
            isFetching.current = true;
            const response = await axios.post(`${webservice}api/getQuestions`);
           // console.log("API Response:", response.data);
            if (response.data.response === 'S') {
                const data = AesDecrypt(response.data.data);
               // console.log("Decrypted Data:", data);
                setQuestions(data);
            } else {
                Swal.fire("Failed to load questions");
                setQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            Swal.fire("Failed to load questions");
            setQuestions([]);
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchQuestions();
    }, []);

    // Get questions for current active tab
    const getCurrentTabQuestions = () => {
        const safeQuestions = Array.isArray(questions) ? questions : [];
        const currentTab = tabs.find(tab => tab.id === activeTab);

        if (!currentTab) return safeQuestions;

        let tabQuestions = safeQuestions.filter(currentTab.filter);

        // Apply additional filters
        tabQuestions = tabQuestions.filter(question => {
            if (!question || typeof question !== 'object') return false;

            const matchesSearch =
                (question.questionText?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (question.questionId?.toLowerCase() || '').includes(searchTerm.toLowerCase());

            const matchesType = filterType === "all" || question.questionType === filterType;
            const matchesStatus = filterStatus === "all" ||
                (filterStatus === "active" && question.isActive) ||
                (filterStatus === "inactive" && !question.isActive);

            return matchesSearch && matchesType && matchesStatus;
        });

        // Sort by order within the tab
        return tabQuestions.sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // Initialize edit form when opening modal
    const handleEdit = (question) => {
        setEditingQuestion(question);
        setEditForm({
            questionId: question.questionId || "", // Set existing ID
            questionText: question.questionText || "",
            questionType: question.questionType || "text",
            options: question.options || [],
            isGeneral: question.isGeneral || false,
            isActive: question.isActive !== undefined ? question.isActive : true,
            relatedTo: question.relatedTo || "",
            helpText: question.helpText || "",
            next: question.next || []
        });
        setNewOption("");
        setShowEditModal(true);
    };

    // Handle new question
    const handleNewQuestion = () => {
        setEditingQuestion(null);
        setEditForm({
            questionId: "", // Reset ID for new question
            questionText: "",
            questionType: "text",
            options: [],
            isGeneral: activeTab === "general", // Auto-set based on current tab
            isActive: true,
            relatedTo: activeTab === "general" ? "" : activeTab,
            helpText: "",
            next: []
        });
        setNewOption("");
        setShowEditModal(true);
    };

    // Handle edit form input changes
    const handleEditInputChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear options when question type changes to non-choice type
        if (field === "questionType" && !["single_select", "multi_select", "dropdown"].includes(value)) {
            setEditForm(prev => ({
                ...prev,
                [field]: value,
                options: []
            }));
        }

        // Auto-set isGeneral when relatedTo changes
        if (field === "relatedTo") {
            setEditForm(prev => ({
                ...prev,
                isGeneral: value === "" || !value
            }));
        }
    };

    // Handle add option
    const handleAddOption = () => {
        if (!newOption.trim()) {
            Swal.fire("Please enter option text");
            return;
        }

        setEditForm(prev => ({
            ...prev,
            options: [...prev.options, newOption.trim()]
        }));
        setNewOption("");
    };

    // Handle remove option
    const handleRemoveOption = (index) => {
        setEditForm(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    // Handle save question (both create and update)
    const handleSaveQuestion = async (e) => {
        e.preventDefault();

        // Validation
        if (!editForm.questionId.trim()) {
            Swal.fire("Question ID is required");
            return;
        }

        if (!editForm.questionText.trim()) {
            Swal.fire("Question text is required");
            return;
        }

        if (["single_select", "multi_select", "dropdown"].includes(editForm.questionType) && editForm.options.length === 0) {
            Swal.fire("Please add at least one option for selected question type");
            return;
        }

        try {
            setSaving(true);

            const payload = {
                questionId: editForm.questionId.trim(),
                questionText: editForm.questionText.trim(),
                questionType: editForm.questionType,
                options: editForm.options,
                isGeneral: editForm.isGeneral,
                isActive: editForm.isActive,
                relatedTo: editForm.relatedTo,
                helpText: editForm.helpText,
                next: editForm.next
            };

            let response;

            if (editingQuestion) {
                // Update existing question
                response = await axios.put(`${webservice}api/updateQuestion/${editingQuestion.questionId}`, payload);
            } else {
                // Create new question
                response = await axios.post(`${webservice}api/createQuestion`, payload);
            }

            if (response.data.response === 'S') {
                Swal.fire({
                    icon: 'success',
                    title: editingQuestion ? 'Question updated!' : 'Question created!',
                    showConfirmButton: false,
                    timer: 1500
                });
                setShowEditModal(false);
                fetchQuestions();
            } else {
                Swal.fire('Error', response.data.message || 'Failed to save question', 'error');
            }
        } catch (error) {
            console.error("Error saving question:", error);
            Swal.fire('Error', 'Failed to save question. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Handle delete question
    const handleDelete = async (questionId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`${webservice}api/deleteQuestion/${questionId}`);
                if (response.data.response === 'S') {
                    Swal.fire('Deleted!', 'Question has been retired.', 'success');
                    fetchQuestions();
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to delete question', 'error');
                }
            } catch (error) {
                console.error("Error deleting question:", error);
                Swal.fire('Error', 'Failed to delete question', 'error');
            }
        }
    };

    // Handle toggle active status
    const handleToggleActive = async (questionId, currentStatus) => {
        try {
            const question = questions.find(q => q.questionId === questionId);
            if (!question) return;

            const response = await axios.put(`${webservice}api/updateQuestion/${questionId}`, {
                ...question,
                isActive: !currentStatus
            });

            if (response.data.response === 'S') {
                fetchQuestions();
            } else {
                Swal.fire('Error', 'Failed to update question status', 'error');
            }
        } catch (error) {
            console.error("Error updating question status:", error);
            Swal.fire('Error', 'Failed to update question status', 'error');
        }
    };

    // Handle move question order within current tab
    const handleMove = async (questionId, direction) => {
        const currentTabQuestions = getCurrentTabQuestions();
        const questionIndex = currentTabQuestions.findIndex(q => q.questionId === questionId);

        if ((direction === -1 && questionIndex === 0) ||
            (direction === 1 && questionIndex === currentTabQuestions.length - 1)) {
            return;
        }

        const targetIndex = questionIndex + direction;
        const targetQuestion = currentTabQuestions[targetIndex];

        // Swap orders
        const updatedQuestions = questions.map(q => {
            if (q.questionId === questionId) {
                return { ...q, order: targetQuestion.order };
            } else if (q.questionId === targetQuestion.questionId) {
                return { ...q, order: currentTabQuestions[questionIndex].order };
            }
            return q;
        });

        setQuestions(updatedQuestions);

        // Update in backend
        try {
            let segment = {};
            if (activeTab === 'general') {
                segment = { isGeneral: true };
            } else {
                segment = { relatedTo: activeTab };
            }

            await axios.put(`${webservice}api/updateQuestionOrder`, {
                questions: [
                    { questionId: questionId, order: targetQuestion.order },
                    { questionId: targetQuestion.questionId, order: currentTabQuestions[questionIndex].order }
                ],
                segment: segment
            });
        } catch (error) {
            console.error("Error updating question order:", error);
            Swal.fire('Error', 'Failed to update question order', 'error');
            fetchQuestions();
        }
    };

    // Stats calculation for current tab
    const currentTabQuestions = getCurrentTabQuestions();

    const stats = {
        total: currentTabQuestions.length,
        active: currentTabQuestions.filter(q => q && q.isActive).length,
        inactive: currentTabQuestions.filter(q => !q.isActive).length,
        types: [...new Set(currentTabQuestions.map(q => q.questionType).filter(Boolean))].length
    };

    // Check if current question type requires options
    const requiresOptions = ["single_select", "multi_select", "dropdown"].includes(editForm.questionType);

    if (loading) {
        return (
            <div className="question-list-container">
                <div className="loading-state">
                    <div>Loading questions...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="question-list-container">
            {/* Header Section */}
            <div className="question-list-header">
                <h1 className="question-list-title">Manage Questions</h1>
                <div className="controls-section">
                    <input
                        type="text"
                        className="search-box"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div style={{ width: '200px' }}>
                        <Select
                            value={filterTypeOptions.find(opt => opt.value === filterType)}
                            onChange={(option) => setFilterType(option.value)}
                            options={filterTypeOptions}
                            className="filter-select-container"
                            classNamePrefix="react-select"
                            isSearchable={false}
                        />
                    </div>
                    <div style={{ width: '200px' }}>
                        <Select
                            value={filterStatusOptions.find(opt => opt.value === filterStatus)}
                            onChange={(option) => setFilterStatus(option.value)}
                            options={filterStatusOptions}
                            className="filter-select-container"
                            classNamePrefix="react-select"
                            isSearchable={false}
                        />
                    </div>
                    <button
                        className="btn btn-new"
                        onClick={handleNewQuestion}
                    >
                        + New Question
                    </button>
                </div>
            </div>

            {/* Tabs Section */}
            <div className="tabs-section">
                <div className="tabs-container">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                            <span className="tab-count">
                                {questions.filter(tab.filter).length}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-number">{stats.total}</div>
                    <div className="stat-label">Total Questions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.active}</div>
                    <div className="stat-label">Active Questions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.inactive}</div>
                    <div className="stat-label">Inactive Questions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats.types}</div>
                    <div className="stat-label">Question Types</div>
                </div>
            </div>

            {/* Questions Table */}
            <div className="questions-table">
                {currentTabQuestions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📝</div>
                        <h3>No questions found in {tabs.find(tab => tab.id === activeTab)?.label}</h3>
                        <p>
                            {questions.length === 0
                                ? "Get started by creating your first question."
                                : "Try creating a question for this category or adjusting your search."}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="table-header">
                            <div>#</div>
                            <div>Question</div>
                            <div>Type</div>
                            <div>Status</div>
                            <div>Actions</div>
                        </div>

                        <AnimatePresence>
                            {currentTabQuestions.map((question, index) => (
                                <motion.div
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                    key={question.questionId || index}
                                    className="table-row"
                                >
                                    <div className="order-cell">{question.order || index + 1}</div>

                                    <div className="question-text-cell">
                                        <div>{question.questionText || 'No question text'}</div>
                                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                            ID: {question.questionId || 'No ID'}
                                        </div>
                                    </div>

                                    <div className="type-cell">{question.questionType || 'unknown'}</div>

                                    <div className={`status-cell ${question.isActive ? 'status-active' : 'status-inactive'}`}>
                                        <div className={`status-dot ${question.isActive ? 'status-dot-active' : 'status-dot-inactive'}`}></div>
                                        {question.isActive ? 'Active' : 'Inactive'}
                                    </div>

                                    <div className="actions-cell">
                                        <button
                                            className="btn btn-move btn-sm"
                                            onClick={() => handleMove(question.questionId, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className="btn btn-move btn-sm"
                                            onClick={() => handleMove(question.questionId, 1)}
                                            disabled={index === currentTabQuestions.length - 1}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            className="btn btn-edit btn-sm"
                                            onClick={() => handleEdit(question)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-toggle btn-sm"
                                            onClick={() => handleToggleActive(question.questionId, question.isActive)}
                                        >
                                            {question.isActive ? 'Disable' : 'Enable'}
                                        </button>
                                        <button
                                            className="btn btn-delete btn-sm"
                                            onClick={() => handleDelete(question.questionId)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">
                                {editingQuestion ? 'Edit Question' : 'Create New Question'}
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowEditModal(false)}
                                disabled={saving}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSaveQuestion} className={saving ? "loading" : ""}>


                                {/* Category Selection - Toggle Segment */}
                                <div className="form-group">
                                    <label className="form-label">Question Category</label>
                                    <div className="toggle-segment">
                                        <button
                                            type="button"
                                            className={`toggle-option ${editForm.isGeneral ? 'active' : ''}`}
                                            onClick={() => setEditForm(prev => ({
                                                ...prev,
                                                isGeneral: true,
                                                relatedTo: ""
                                            }))}
                                            disabled={saving}
                                        >
                                            <span className="toggle-icon">🌐</span>
                                            General Question
                                            <span className="toggle-description">Shown to all users</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`toggle-option ${!editForm.isGeneral ? 'active' : ''}`}
                                            onClick={() => setEditForm(prev => ({
                                                ...prev,
                                                isGeneral: false,
                                                relatedTo: prev.relatedTo || "mood_monitor"
                                            }))}
                                            disabled={saving}
                                        >
                                            <span className="toggle-icon">🎯</span>
                                            Related to Health Category
                                            <span className="toggle-description">Specific health tracking</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Related To Dropdown - Only show when not general */}
                                {!editForm.isGeneral && (
                                    <div className="form-group">
                                        <label className="form-label required">Select Health Category</label>
                                        <Select
                                            value={relatedToOptions.find(opt => opt.value === editForm.relatedTo) || relatedToOptions[1]}
                                            onChange={(e) => handleEditInputChange("relatedTo", e.value)}
                                            options={relatedToOptions.filter(opt => opt.value !== "")}
                                            isDisabled={saving}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                )}
                                {/* Question ID Input */}
                                <div className="form-group">
                                    <label className="form-label required">Question ID</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={editForm.questionId}
                                        onChange={(e) => handleEditInputChange("questionId", e.target.value)}
                                        placeholder="Enter unique Question ID (e.g., Q-001)"
                                        required
                                        disabled={saving || editingQuestion} // Disable specific ID editing if needed, generally ID shouldn't change
                                    />
                                    {editingQuestion && <small style={{ color: '#718096' }}>Question ID cannot be changed once created.</small>}
                                </div>

                                {/* Help Text */}
                                <div className="form-group">
                                    <label className="form-label">Help Text</label>
                                    <textarea
                                        className="form-textarea"
                                        value={editForm.helpText}
                                        onChange={(e) => handleEditInputChange("helpText", e.target.value)}
                                        placeholder="Enter help text for the user..."
                                        rows={2}
                                    />
                                </div>

                                {/* Next Question Logic (Active Tab Questions Only) */}
                                <div className="form-group">
                                    <label className="form-label">Next Question</label>
                                    <Select
                                        value={currentTabQuestions.filter(q => (editForm.next || []).includes(q.questionId)).map(q => ({ value: q.questionId, label: `${q.questionId} - ${q.questionText.substring(0, 30)}...` }))}
                                        onChange={(selectedOptions) => handleEditInputChange("next", selectedOptions ? selectedOptions.map(opt => opt.value) : [])}
                                        options={currentTabQuestions.filter(q => q.questionId !== editForm.questionId).map(q => ({
                                            value: q.questionId,
                                            label: `${q.questionId} - ${q.questionText.substring(0, 30)}...`
                                        }))}
                                        isMulti
                                        placeholder="Select next question(s)..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        closeMenuOnSelect={false}
                                    />
                                    <small style={{ color: '#718096', display: 'block', marginTop: '4px' }}>
                                        Leave empty for default flow. Select multiple for logic branching.
                                    </small>
                                </div>
                                {/* Question Text */}
                                <div className="form-group">
                                    <label className="form-label required">Question Text</label>
                                    <textarea
                                        className="form-textarea"
                                        value={editForm.questionText}
                                        onChange={(e) => handleEditInputChange("questionText", e.target.value)}
                                        placeholder="Enter your question here..."
                                        required
                                        disabled={saving}
                                    />
                                </div>

                                {/* Question Type */}
                                <div className="form-group">
                                    <label className="form-label required">Question Type</label>
                                    <Select
                                        value={questionTypeOptions.find(opt => opt.value === editForm.questionType) || questionTypeOptions[0]}
                                        onChange={(e) => handleEditInputChange("questionType", e.value)}
                                        options={questionTypeOptions}
                                        isDisabled={saving}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                {/* Options for choice-based questions */}
                                {requiresOptions && (
                                    <div className="options-section">
                                        <label className="form-label">
                                            Options {["single_select", "multi_select", "dropdown"].includes(editForm.questionType) && "(Required)"}
                                        </label>



                                        {/* Manual options for single_select, multi_select, dropdown */}
                                        {["single_select", "multi_select", "dropdown"].includes(editForm.questionType) && (
                                            <>
                                                <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={newOption}
                                                        onChange={(e) => setNewOption(e.target.value)}
                                                        placeholder="Enter option text"
                                                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddOption())}
                                                        disabled={saving}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline btn-small"
                                                        onClick={handleAddOption}
                                                        disabled={saving}
                                                    >
                                                        + Add
                                                    </button>
                                                </div>

                                                {editForm.options.length > 0 && (
                                                    <div className="options-list">
                                                        {editForm.options.map((option, index) => (
                                                            <div key={index} className="option-item">
                                                                <span className="option-text">{option}</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-small"
                                                                    onClick={() => handleRemoveOption(index)}
                                                                    disabled={saving}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}


                                    </div>
                                )}

                                {/* Checkbox for Active Status */}
                                <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                                    <div className="checkbox-group">
                                        <input
                                            type="checkbox"
                                            id="isActive"
                                            checked={editForm.isActive}
                                            onChange={(e) => handleEditInputChange("isActive", e.target.checked)}
                                            disabled={saving}
                                        />
                                        <label htmlFor="isActive" className="checkbox-label">
                                            Active Question
                                        </label>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="buttons-group">
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving ? "Saving..." : (editingQuestion ? "Update Question" : "Create Question")}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowEditModal(false)}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}